<template>
  <div class="search-box" :class="{ focused: focused }">
    <input
      ref="baseSearchInput"
      class="base-input search-input"
      :data-test-id="`${props.testId}__BaseSearchInput`"
      :placeholder="placeholder"
      :value="internalValue"
      @focus="focused = true"
      @blur="focused = false"
      @input="inputHandler($event)"
    >
    <div class="search-icon"><BaseIcon name="search" type="disabled" size="24" /></div>
  </div>
</template>

<script lang="ts" setup>
  import { useDebounceFn } from '@vueuse/core'
  import { onMounted, ref, toRef, watch } from 'vue'

  import BaseIcon from '@/components/BaseIcon.vue'

  const props = defineProps({
    debounceAfter: {
      default: 500,
      type: Number,
    },
    forceFocus: {
      default: false,
      type: Boolean,
    },
    modelValue: {
      required: true,
      type: String,
    },
    placeholder: {
      default: '',
      type: String,
    },
    testId: {
      required: true,
      type: String,
    },
  })
  const baseSearchInput = ref<HTMLInputElement>()
  const focused = ref(false)
  const internalValue = toRef(props.modelValue)
  const emit = defineEmits(['update:modelValue'])

  const updateValue = useDebounceFn(() => {
    emit('update:modelValue', internalValue.value)
  }, props.debounceAfter)

  const inputHandler = (event: Event) => {
    internalValue.value = (event.target as HTMLInputElement).value
    void updateValue()
  }

  defineExpose({
    focus: () => {
      if (baseSearchInput.value) {
        baseSearchInput.value.focus()
      }
    },
  })

  onMounted(() => {
    if (props.forceFocus && baseSearchInput.value) {
      baseSearchInput.value.focus()
    }
  })

  watch(
    () => props.modelValue,
    value => {
      internalValue.value = value
    },
  )
</script>

<style scoped>
  .search-box {
    position: relative;
    width: 100%;
    height: 32px;
    font-family: Montserrat-Medium;
  }

  .search-input {
    display: block;
    width: 100%;
    padding: 4px 32px 4px 8px;
    font-size: 14px;
    line-height: 23px;
  }

  .search-input::placeholder {
    font-size: 14px;
    color: var(--color-tertiary-text);
  }

  .search-icon {
    position: absolute;
    top: 4px;
    right: 4px;
    color: var(--color-tertiary-text);
  }

  .focused .search-icon {
    color: var(--color-main-100);
  }
</style>
